import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  EditOutlined,
  EyeOutlined,
  MailOutlined,
  KeyOutlined
} from '@ant-design/icons';
import { Modal, Popover, Spin } from 'antd';
import { AxiosError } from 'axios';
import React, { useState } from 'react';

import { UserR, usersService } from '../../../services/users';
import { openSuccessNotification } from '../../../utils/notifications';
import { showApiErrors } from '../../../utils/showApiErrors';
import { ViewPermissionsModal } from './ViewPermissionsModal';

type UsersTableActionsProps = {
  user: UserR;
  onEditClick: () => void;
  onDeactivate: () => void;
  onActivate: () => void;
  onManageTokens: () => void;
};

export const UsersTableActions = ({
  user,
  onEditClick,
  onDeactivate,
  onActivate,
  onManageTokens
}: UsersTableActionsProps): JSX.Element => {
  const [loadingDeactivate, setLoadingDeactivate] = useState(false);
  const [loadingActivate, setLoadingActivate] = useState(false);
  const [viewPermissionsModalVisible, setViewPermissionsModalVisible] = useState(false);

  const handleDeactivate = async () => {
    setLoadingDeactivate(true);
    try {
      await usersService.deactivate(user.id);
      openSuccessNotification({ message: 'Successfully deactivated user!', duration: 8 });

      onDeactivate();
    } catch (e) {
      showApiErrors(e as AxiosError);
    } finally {
      setLoadingDeactivate(false);
    }
  };

  const handleActivate = async () => {
    setLoadingActivate(true);
    try {
      await usersService.activate(user.id);
      openSuccessNotification({ message: 'Successfully activated user!', duration: 8 });

      onActivate();
    } catch (e) {
      showApiErrors(e as AxiosError);
    } finally {
      setLoadingActivate(false);
    }
  };

  const handleResendSetupMail = async () => {
    try {
      await usersService.resendSetupMail(user.id);
      openSuccessNotification({ message: 'Successfully resend setup mail!', duration: 8 });
    } catch (e) {
      showApiErrors(e as AxiosError);
    }
  };

  const handleOpenDeactivateModal = () => {
    Modal.confirm({
      title: 'Are you sure that you want to deactivate this user?',
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      onOk: () => handleDeactivate(),
      onCancel: () => {}
    });
  };

  const handleOpenActivateModal = () => {
    Modal.confirm({
      title: 'Are you sure that you want to activate this user?',
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      onOk: () => handleActivate(),
      onCancel: () => {}
    });
  };

  const handleOpenResendSetupMailModal = () => {
    Modal.confirm({
      title: 'Are you sure that you want to resend the setup mail to this user?',
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      onOk: () => handleResendSetupMail(),
      onCancel: () => {}
    });
  };

  const actionClassName =
    'inline-flex items-center justify-center rounded-md cursor-pointer text-base p-1 sm:p-0 sm:text-sm hover:bg-gray-100';
  const iconStyle = { fontSize: 'inherit' };

  return (
    <Spin spinning={loadingDeactivate || loadingActivate}>
      <div
        className="flex justify-center items-center flex-nowrap gap-3 sm:gap-2 text-xl sm:text-base"
        onClick={(e) => e.stopPropagation()}
      >
        <Popover content="Preview/Edit" trigger="hover">
          <EditOutlined className={actionClassName} style={{ ...iconStyle, color: '#438EF7' }} onClick={onEditClick} />
        </Popover>

        <Popover content="View permissions" trigger="hover">
          <EyeOutlined
            className={actionClassName}
            style={iconStyle}
            onClick={() => setViewPermissionsModalVisible(true)}
          />
        </Popover>
        {viewPermissionsModalVisible && (
          <ViewPermissionsModal
            modalVisible={viewPermissionsModalVisible}
            userId={user.id}
            onClose={() => setViewPermissionsModalVisible(false)}
          />
        )}

        {user.isActive ? (
          <Popover content="Deactivate" trigger="hover">
            <CloseCircleOutlined
              className={actionClassName}
              style={{ ...iconStyle, color: 'red' }}
              onClick={handleOpenDeactivateModal}
            />
          </Popover>
        ) : (
          <Popover content="Activate" trigger="hover">
            <CheckCircleOutlined
              className={actionClassName}
              style={{ ...iconStyle, color: 'green' }}
              onClick={handleOpenActivateModal}
            />
          </Popover>
        )}
        <Popover content="Resend setup mail" trigger="hover">
          <MailOutlined
            className={actionClassName}
            style={{ ...iconStyle, color: 'blue' }}
            onClick={handleOpenResendSetupMailModal}
          />
        </Popover>
        <Popover content="Manage tokens" trigger="hover">
          <KeyOutlined className={actionClassName} style={{ ...iconStyle, color: 'orange' }} onClick={onManageTokens} />
        </Popover>
      </div>
    </Spin>
  );
};
